import React, { lazy, Suspense } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import './Home.css';
import { FaLinkedin, FaGithub, FaWhatsapp, FaArrowRight } from 'react-icons/fa';
import { SiUpwork, SiFreelancer } from 'react-icons/si';
import profilePic from '../images/profile3.png';
import { Link } from 'react-router-dom';
import { logCustomEvent } from '../firebase';
import { isMobileDevice } from '../utils/isMobileDevice';
import MobileHome from './MobileHome';

const ParticleBackground = lazy(() => import('./ParticleBackground'));
const FloatingElements = lazy(() => import('./FloatingElements'));

const jobTitle = 'Flutter Expert & Web Developer | AI/LLM Expert.';

const DesktopHome = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section id="home" className="home-section">
      <Suspense fallback={null}>
        <ParticleBackground id="home-particles" />
        <FloatingElements />
      </Suspense>
      <motion.div
        className="home-content"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ opacity }}
      >
        <motion.div className="left-side" style={{ y }}>
          <motion.h2 variants={itemVariants}>Hello, it&apos;s Junaid Nasir</motion.h2>
          <motion.h1 className="animated-title" variants={itemVariants}>
            And I&apos;m a <span className="typing">{jobTitle}</span>
          </motion.h1>
          <motion.p variants={itemVariants}>
            I&apos;m passionate about creating clean and user-friendly mobile applications that provide
            seamless experiences. My goal is to bridge the gap between user needs and technological solutions.
          </motion.p>
          <motion.div className="social-icons2" variants={itemVariants}>
            <motion.a
              href="https://www.linkedin.com/in/junaid-nasir-a44b03203/?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, y: -5 }}
              whileTap={{ scale: 0.9 }}
              className="hoverable"
              onClick={() => logCustomEvent('click_social_link', { platform: 'linkedin', location: 'home' })}
            >
              <FaLinkedin className="icon" />
            </motion.a>
            <motion.a
              href="https://github.com/junaid8262?tab=repositories"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, y: -5 }}
              whileTap={{ scale: 0.9 }}
              className="hoverable"
              onClick={() => logCustomEvent('click_social_link', { platform: 'github', location: 'home' })}
            >
              <FaGithub className="icon" />
            </motion.a>
            <motion.a
              href="https://www.upwork.com/freelancers/~0136f4598139fcbb51"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, y: -5 }}
              whileTap={{ scale: 0.9 }}
              className="hoverable"
              onClick={() => logCustomEvent('click_social_link', { platform: 'upwork', location: 'home' })}
            >
              <SiUpwork className="icon" />
            </motion.a>
            <motion.a
              href="https://www.freelancer.com/u/junaid8282"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, y: -5 }}
              whileTap={{ scale: 0.9 }}
              className="hoverable"
              onClick={() => logCustomEvent('click_social_link', { platform: 'freelancer', location: 'home' })}
            >
              <SiFreelancer className="icon" />
            </motion.a>
            <motion.a
              href="https://wa.me/923116741249"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, y: -5 }}
              whileTap={{ scale: 0.9 }}
              className="hoverable"
              onClick={() => logCustomEvent('click_social_link', { platform: 'whatsapp', location: 'home' })}
            >
              <FaWhatsapp className="icon" />
            </motion.a>
          </motion.div>
          <motion.div className="portfolio-btn-wrapper" style={{ marginTop: '45px' }} variants={itemVariants}>
            <Link
              to="/projects"
              className="btn-portfolio hoverable"
              onClick={() => logCustomEvent('click_view_portfolio', { location: 'home' })}
            >
              <span>View Portfolio</span>
              <FaArrowRight className="btn-arrow" />
            </Link>
          </motion.div>
        </motion.div>
        <motion.div
          className="right-side"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <div className="profile-pic-wrapper">
            <div className="profile-pic-backdrop" aria-hidden="true" />
            <div className="profile-pic-glow" aria-hidden="true" />
            <motion.img
              src={profilePic}
              alt="Profile"
              className="profile-pic"
              whileHover={{ scale: 1.03 }}
              transition={{ type: 'spring', stiffness: 300 }}
            />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

const Home = () => (isMobileDevice() ? <MobileHome /> : <DesktopHome />);

export default Home;
